// @flow strict

import React from 'react';

import {
  SvgIcon,
  type SvgIconComponentProps,
} from '~components/icons/svg-icon';

import { FacebookIcon } from '~components/icons/social-media/facebook-icon';
import { InstagramIcon } from '~components/icons/social-media/instagram-icon';
import { LinkedinIcon } from '~components/icons/social-media/linkedin-icon';
import { TwitterIcon } from '~components/icons/social-media/twitter-icon';
import { XingIcon } from '~components/icons/social-media/xing-icon';
import { YoutubeIcon } from '~components/icons/social-media/youtube-icon';

import { capitalize } from '~utils/helpers/capitalize';

import type { SocialMediaType } from '~types/author';

/**
 * Type for icon colors
 */
type SocialMediaIconColor = 'black' | 'dark' | 'gray' | 'orange';

/**
 * Type for component props
 */
type SocialMediaIconProps = {
  type: SocialMediaType,
  color?: SocialMediaIconColor,
  hoverColor?: SocialMediaIconColor,
  svgProps?: SvgIconComponentProps,
};

/**
 * Type for svg icon props.
 */
export type SocialMediaSVGProps = {
  color: string,
};

/**
 * Mapping for names an hex values of colors.
 */
const colorMapping = {
  black: '#000000',
  dark: '#454545',
  gray: '#d8d8d8',
  orange: '#ff6602',
};

/**
 * Mapping for type and components.
 */
const componentMapping = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  twitter: TwitterIcon,
  xing: XingIcon,
  youtube: YoutubeIcon,
  pinterest: null,
};

/**
 * Wrapper component for social media icons.
 * Renders social media icon with given size & color, and adds hover style.
 *
 * @param {SocialMediaIconProps} props - component properties
 *
 * @returns {Node}
 */
export const SocialMediaIcon = (props: SocialMediaIconProps) => {
  const { type, color = 'black', hoverColor = 'orange', svgProps } = props;

  // get svg component for type
  const SVGComponent = componentMapping[type];
  if (SVGComponent == null) {
    return null;
  }

  // create style with hover color
  const style = `svg:hover .${type} { fill: ${colorMapping[hoverColor]}; }`;

  return (
    <SvgIcon
      viewBox="0 0 35 35"
      name={`OMQ${capitalize(type)}Icon`}
      {...svgProps}
    >
      <style dangerouslySetInnerHTML={{ __html: style }} />
      <SVGComponent color={colorMapping[color]} />
    </SvgIcon>
  );
};
